import React, {useCallback, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {
  adminGetReviews,
  adminUpdateReviewStatus,
  adminDeleteReview,
} from "../../api";
import "./AdminLayout.css";

const TABS = [
  {value: "pending", label: "Pending"},
  {value: "approved", label: "Approved"},
  {value: "rejected", label: "Rejected"},
  {value: "", label: "All"},
];

const STATUS_COLORS = {
  pending: "badge-yellow",
  approved: "badge-green",
  rejected: "badge-red",
};

export default function AdminReviews() {
  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({pending: 0, approved: 0, rejected: 0});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminGetReviews(tab)
      .then(({data}) => {
        setReviews(data.reviews);
        setCounts(data.counts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  async function setStatus(id, status) {
    setUpdating(id);
    try {
      await adminUpdateReviewStatus(id, status);
      load();
    } catch {
      alert("Update failed");
    } finally {
      setUpdating(null);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this review permanently?")) return;
    setUpdating(id);
    try {
      await adminDeleteReview(id);
      load();
    } catch {
      alert("Delete failed");
    } finally {
      setUpdating(null);
    }
  }

  const all = counts.pending + counts.approved + counts.rejected;

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Reviews</h1>
          <p className="admin-page-sub">
            {counts.pending} awaiting approval · {counts.approved} live
          </p>
        </div>
        <button className="btn-secondary" onClick={load}>
          ↻ Refresh
        </button>
      </div>

      <div style={{padding: "16px 28px 0", display: "flex", gap: 8, flexWrap: "wrap"}}>
        {TABS.map((t) => {
          const n = t.value ? counts[t.value] : all;
          return (
            <button
              key={t.label}
              className={tab === t.value ? "btn-primary" : "btn-secondary"}
              onClick={() => setTab(t.value)}
            >
              {t.label} ({n})
            </button>
          );
        })}
      </div>

      <div style={{padding: "16px 28px 28px"}}>
        <div className="admin-card">
          {loading ? (
            <div className="admin-loading">Loading…</div>
          ) : reviews.length === 0 ? (
            <div className="empty-state">
              <span>⭐</span>
              <p>{tab === "pending" ? "You're all caught up." : "No reviews here yet."}</p>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Rating</th>
                    <th>Review</th>
                    <th>Product</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((r) => (
                    <tr key={r._id}>
                      <td style={{color: "#f2a516", whiteSpace: "nowrap", fontSize: 15}}>
                        {"★".repeat(r.rating)}
                        <span style={{color: "#e5e7eb"}}>{"★".repeat(5 - r.rating)}</span>
                      </td>
                      <td style={{maxWidth: 340}}>
                        {r.title && <strong style={{display: "block", marginBottom: 4}}>{r.title}</strong>}
                        <span style={{fontSize: 13, color: "#4b5563", whiteSpace: "pre-line"}}>
                          {r.body}
                        </span>
                      </td>
                      <td style={{fontSize: 13}}>
                        {r.product ? (
                          <Link
                            to={`/products/${r.product.slug || r.product._id}`}
                            target="_blank"
                            style={{color: "#f59e0b", textDecoration: "none"}}
                          >
                            {r.product.name}
                          </Link>
                        ) : (
                          <span style={{color: "#9ca3af"}}>Deleted product</span>
                        )}
                      </td>
                      <td style={{fontSize: 13}}>
                        <strong>{r.name}</strong>
                        <div style={{color: "#6b7280"}}>{r.email}</div>
                        {r.verified && (
                          <span className="badge badge-green" style={{marginTop: 4}}>
                            Verified buyer
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                      </td>
                      <td style={{fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap"}}>
                        {new Date(r.createdAt).toLocaleDateString("en-IN")}
                      </td>
                      <td>
                        <div style={{display: "flex", gap: 6}}>
                          {r.status !== "approved" && (
                            <button
                              className="btn-primary"
                              disabled={updating === r._id}
                              onClick={() => setStatus(r._id, "approved")}
                            >
                              Approve
                            </button>
                          )}
                          {r.status !== "rejected" && (
                            <button
                              className="btn-secondary"
                              disabled={updating === r._id}
                              onClick={() => setStatus(r._id, "rejected")}
                            >
                              {r.status === "approved" ? "Hide" : "Reject"}
                            </button>
                          )}
                          <button
                            className="btn-danger"
                            disabled={updating === r._id}
                            onClick={() => handleDelete(r._id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
